import type {
  LabelPrintFormat,
  LabelPrintFormatGroup,
  LabelPrintMarginSettings,
  LabelPrinterProfile,
  PrinterModelPreset,
} from "@itaas/label-print";
import {
  DEFAULT_LABEL_PRINT_FORMAT_ID,
  getLabelPrintFormat,
  LABEL_PRINT_FORMATS,
  matchPrinterModelPreset,
  mergeMarginSettings,
  normalizePrinterKey,
} from "@itaas/label-print";
import type { LabelPrintRotation } from "./label-print-rotation";

const STORAGE_KEY = "itaas.labelPrint.settings";

export interface LabelPrintUserSettings {
  formatId: string;
  rotation: LabelPrintRotation;
  margins: LabelPrintMarginSettings;
}

const DEFAULT_SETTINGS: LabelPrintUserSettings = {
  formatId: DEFAULT_LABEL_PRINT_FORMAT_ID,
  rotation: 0,
  margins: {},
};

export function loadLabelPrintUserSettings(): LabelPrintUserSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<LabelPrintUserSettings>;
    const formatId = parsed.formatId ?? DEFAULT_SETTINGS.formatId;
    if (!getLabelPrintFormat(formatId)) {
      return { ...DEFAULT_SETTINGS };
    }
    return {
      formatId,
      rotation: (parsed.rotation ?? 0) as LabelPrintRotation,
      margins: parsed.margins ?? {},
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveLabelPrintUserSettings(settings: LabelPrintUserSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
}

export function settingsFromPrinterPreset(preset: PrinterModelPreset): LabelPrintUserSettings {
  return {
    formatId: preset.formatId,
    rotation: (preset.rotation ?? 0) as LabelPrintRotation,
    margins: { ...preset.margins },
  };
}

export function settingsFromBridgeProfile(profile: LabelPrinterProfile): LabelPrintUserSettings | null {
  if (!getLabelPrintFormat(profile.formatId)) return null;
  return {
    formatId: profile.formatId,
    rotation: (profile.rotation ?? 0) as LabelPrintRotation,
    margins: profile.margins ?? {},
  };
}

export function resolveSettingsForPrinter(
  printerName: string,
  driverName: string | undefined,
  bridgeProfiles: Record<string, LabelPrinterProfile> | undefined,
  current: LabelPrintUserSettings,
): LabelPrintUserSettings {
  const saved = bridgeProfiles?.[normalizePrinterKey(printerName)];
  if (saved) {
    const fromBridge = settingsFromBridgeProfile(saved);
    if (fromBridge) return fromBridge;
  }
  const preset = matchPrinterModelPreset(printerName, driverName);
  if (preset) {
    return settingsFromPrinterPreset(preset);
  }
  return current;
}

export function mergeUserMarginPatch(
  current: LabelPrintMarginSettings,
  patch: Partial<LabelPrintMarginSettings>,
): LabelPrintMarginSettings {
  return mergeMarginSettings(current, patch);
}

export interface DynamicLabelFormats {
  formats: LabelPrintFormat[];
  groups: LabelPrintFormatGroup[];
}

export function fallbackLabelFormats(): DynamicLabelFormats {
  return {
    formats: LABEL_PRINT_FORMATS,
    groups: [],
  };
}

/** @deprecated utilisez loadLabelPrintUserSettings */
export function loadSavedLabelFormat(): LabelPrintFormat {
  const settings = loadLabelPrintUserSettings();
  return getLabelPrintFormat(settings.formatId) ?? getLabelPrintFormat(DEFAULT_LABEL_PRINT_FORMAT_ID)!;
}

/** @deprecated utilisez saveLabelPrintUserSettings */
export function saveLabelFormat(formatId: string): void {
  const current = loadLabelPrintUserSettings();
  saveLabelPrintUserSettings({ ...current, formatId });
}
